import { computed, onMounted, reactive, ref, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { buildRequestErrorMessage } from './shared';

const trimValue = (value) => String(value ?? '').trim();

const decodeUnicodeEscapes = (value) => String(value ?? '').replace(
  /\\u([0-9a-fA-F]{4})/g,
  (_, code) => String.fromCharCode(Number.parseInt(code, 16)),
);

const formatLegacyValueForView = (value) => {
  if (typeof value !== 'string') {
    return JSON.stringify(value ?? '', null, 2);
  }

  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object') {
      return JSON.stringify(parsed, null, 2);
    }
  } catch (_) {
    // 非 JSON 日志继续按普通文本展示。
  }

  return decodeUnicodeEscapes(value);
};

const normalizePage = (value) => {
  const page = Number(value || 1);
  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
};

const getDefaultValueByTarget = (target, valueType) => {
  if (!target) return '';
  if (target.id === 'Security_num' || target.id === 'Crawler_num') return '1';
  if (target.id === 'filename_find' || target.id === 'vin_find' || target.id === 'car_find' || target.id === 'OFFlineID') return '1';
  return '';
};

export function useMazdaLegacyRedis(mazdaPost, serverName) {
  const legacyOverview = ref([]);
  const legacyTargetId = ref('');
  const legacyItems = ref([]);
  const legacyTotal = ref(0);
  const legacyPage = ref(1);
  const legacyPageSize = ref(50);
  const legacyKeyword = ref('');
  const legacyJumpPage = ref('1');
  const legacyCursor = ref('0');
  const legacyNextCursor = ref('0');
  const legacyCursorHistory = ref([]);
  const legacyHasMore = ref(false);
  const legacyScanMode = ref(false);
  const legacyExactTotal = ref(true);
  const legacyNotice = ref('');
  const legacyFieldExact = ref(false);
  const legacyLoading = ref(false);
  const legacySaving = ref(false);
  const legacyReadOnly = ref(false);
  const legacyDialogVisible = ref(false);
  const legacyEditMode = ref('add');
  const legacyForm = reactive({
    fullKey: '',
    valueType: 'string',
    field: '',
    oldField: '',
    member: '',
    oldMember: '',
    value: '',
    deleteWholeKey: false,
  });

  const selectedLegacyTarget = computed(() => legacyOverview.value.find((item) => item.id === legacyTargetId.value) || null);
  const legacyAllowAdd = computed(() => Boolean(selectedLegacyTarget.value?.allowAdd));
  const legacyTargetOptions = computed(() => legacyOverview.value.map((item) => ({
    label: item.title,
    value: item.id,
    group: item.group,
    count: item.count ?? 0,
    description: item.description || '',
    redisType: item.redisType || '',
  })));
  const legacyCanUseFieldExact = computed(() => Boolean(selectedLegacyTarget.value?.redisType === 'hash' && selectedLegacyTarget.value?.key));
  const legacyTotalPages = computed(() => Math.max(1, Math.ceil((legacyTotal.value || 0) / (legacyPageSize.value || 50))));
  const legacyPagerLabel = computed(() => {
    if (legacyScanMode.value) {
      if (legacyExactTotal.value) {
        return `第 ${legacyPage.value} 批 / 共 ${legacyTotal.value || 0} 条`;
      }
      return `第 ${legacyPage.value} 批 / 当前批 ${legacyItems.value.length} 条`;
    }
    return `当前第 ${legacyPage.value} / ${legacyTotalPages.value} 页`;
  });

  const legacyValueTypeOptions = computed(() => {
    const target = selectedLegacyTarget.value;
    if (!target) return ['string'];
    if (target.redisType === 'hash') return ['hash'];
    if (target.redisType === 'string') return ['string'];
    if (target.redisType === 'set') return ['set'];
    return [target.redisType || 'string'];
  });

  const legacyCanSave = computed(() => {
    if (legacyForm.deleteWholeKey) return false;
    if (!legacyForm.fullKey || legacyForm.valueType === 'list') return false;
    if (legacyForm.valueType === 'hash') return Boolean(trimValue(legacyForm.field));
    if (legacyForm.valueType === 'set') return Boolean(trimValue(legacyForm.member || legacyForm.value));
    return legacyForm.valueType === 'string';
  });

  const legacyFullKeyReadonly = computed(() => {
    const target = selectedLegacyTarget.value;
    if (legacyEditMode.value === 'edit') return true;
    if (!target) return false;
    return Boolean(target.key) || target.id === 'state_strings';
  });

  const legacyFieldReadonly = computed(() => legacyEditMode.value === 'edit' && legacyForm.valueType === 'hash');
  const legacyDialogTitle = computed(() => {
    if (legacyReadOnly.value) return '查看日志及其他数据';
    return legacyEditMode.value === 'add' ? '新增日志及其他数据' : '编辑日志及其他数据';
  });

  const resetLegacyPagination = () => {
    legacyPage.value = 1;
    legacyJumpPage.value = '1';
    legacyCursor.value = '0';
    legacyNextCursor.value = '0';
    legacyCursorHistory.value = [];
    legacyHasMore.value = false;
    legacyNotice.value = '';
  };

  const resetLegacyListState = () => {
    legacyItems.value = [];
    legacyTotal.value = 0;
    legacyHasMore.value = false;
    legacyNextCursor.value = '0';
    legacyNotice.value = '';
  };

  const loadLegacyOverview = async () => {
    try {
      const response = await mazdaPost('legacyRedisOverview');
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '日志及其他数据概览读取失败');
        legacyOverview.value = [];
        return;
      }
      legacyOverview.value = Array.isArray(data.items) ? data.items : [];
      if (!legacyTargetId.value && legacyOverview.value.length) {
        legacyTargetId.value = legacyOverview.value[0].id;
      }
      if (legacyTargetId.value && !legacyOverview.value.some((item) => item.id === legacyTargetId.value)) {
        legacyTargetId.value = legacyOverview.value[0]?.id || '';
      }
    } catch (error) {
      legacyOverview.value = [];
      ElMessage.error(buildRequestErrorMessage('日志及其他数据概览读取失败', error));
    }
  };

  const loadLegacyItems = async () => {
    if (!legacyTargetId.value) {
      resetLegacyListState();
      return;
    }

    const target = selectedLegacyTarget.value;
    const useCursorMode = Boolean(target?.redisType === 'hash' && target?.key);
    legacyLoading.value = true;
    try {
      const payload = {
        targetId: legacyTargetId.value,
        keyword: legacyKeyword.value,
        page: legacyPage.value,
        pageSize: legacyPageSize.value,
      };

      if (useCursorMode) {
        payload.cursorMode = true;
        payload.cursor = legacyCursor.value || '0';
        payload.fieldExact = Boolean(legacyFieldExact.value && trimValue(legacyKeyword.value));
      }

      const response = await mazdaPost('legacyRedisList', payload);
      const data = response.data || {};
      if (data.success) {
        legacyItems.value = Array.isArray(data.items)
          ? data.items.map((item) => ({
            ...item,
            valuePreview: decodeUnicodeEscapes(item?.valuePreview),
          }))
          : [];
        legacyTotal.value = Number(data.total || 0);
        legacyPage.value = normalizePage(data.page || legacyPage.value);
        legacyPageSize.value = Number(data.pageSize || legacyPageSize.value);
        legacyJumpPage.value = String(legacyPage.value);
        legacyScanMode.value = Boolean(data.scanMode);
        legacyExactTotal.value = data.exactTotal !== false;
        legacyHasMore.value = Boolean(data.hasMore);
        legacyNextCursor.value = String(data.nextCursor ?? '0');
        legacyNotice.value = data.notice || '';
      } else {
        resetLegacyListState();
        legacyScanMode.value = false;
        legacyExactTotal.value = true;
        ElMessage.error(data.message || '日志及其他数据读取失败');
      }
    } catch (error) {
      resetLegacyListState();
      legacyScanMode.value = false;
      legacyExactTotal.value = true;
      ElMessage.error(buildRequestErrorMessage('日志及其他数据读取失败', error));
    } finally {
      legacyLoading.value = false;
    }
  };

  const refreshLegacyAll = async () => {
    resetLegacyPagination();
    await loadLegacyOverview();
    await loadLegacyItems();
  };

  const handleLegacyTargetChange = async () => {
    legacyKeyword.value = '';
    legacyFieldExact.value = false;
    legacyScanMode.value = false;
    legacyExactTotal.value = true;
    resetLegacyPagination();
    await loadLegacyItems();
  };

  const selectLegacyTarget = async (targetId) => {
    if (!targetId || targetId === legacyTargetId.value) return;
    legacyTargetId.value = targetId;
    await handleLegacyTargetChange();
  };

  const handleLegacySearch = async () => {
    resetLegacyPagination();
    await loadLegacyItems();
  };

  const handleLegacyPageChange = async (page) => {
    if (legacyScanMode.value) return;
    legacyPage.value = normalizePage(page);
    legacyJumpPage.value = String(legacyPage.value);
    await loadLegacyItems();
  };

  const handleLegacySizeChange = async (size) => {
    legacyPageSize.value = Number(size || 50);
    resetLegacyPagination();
    await loadLegacyItems();
  };

  const handleLegacyJump = async () => {
    if (legacyScanMode.value) return;
    const page = Number(legacyJumpPage.value);
    if (!Number.isFinite(page) || page < 1) {
      legacyJumpPage.value = String(legacyPage.value);
      return;
    }
    legacyPage.value = Math.min(legacyTotalPages.value, Math.max(1, Math.floor(page)));
    legacyJumpPage.value = String(legacyPage.value);
    await loadLegacyItems();
  };

  const handleLegacyNextBatch = async () => {
    if (!legacyScanMode.value || !legacyHasMore.value || legacyLoading.value) return;
    legacyCursorHistory.value.push(legacyCursor.value || '0');
    legacyCursor.value = legacyNextCursor.value || '0';
    legacyPage.value += 1;
    legacyJumpPage.value = String(legacyPage.value);
    await loadLegacyItems();
  };

  const handleLegacyPrevBatch = async () => {
    if (!legacyScanMode.value || !legacyCursorHistory.value.length || legacyLoading.value) return;
    legacyCursor.value = legacyCursorHistory.value.pop() || '0';
    legacyPage.value = Math.max(1, legacyPage.value - 1);
    legacyJumpPage.value = String(legacyPage.value);
    await loadLegacyItems();
  };

  const resetLegacyForm = () => {
    legacyForm.fullKey = '';
    legacyForm.valueType = 'string';
    legacyForm.field = '';
    legacyForm.oldField = '';
    legacyForm.member = '';
    legacyForm.oldMember = '';
    legacyForm.value = '';
    legacyForm.deleteWholeKey = false;
  };

  const openLegacyAdd = () => {
    const target = selectedLegacyTarget.value;
    if (!target) {
      ElMessage.warning('请先选择维护分类');
      return;
    }
    if (!target.allowAdd) {
      ElMessage.warning('当前分类不支持新增，可在列表中编辑已有 Key 或删除整个 Key');
      return;
    }

    resetLegacyForm();
    legacyReadOnly.value = false;
    legacyEditMode.value = 'add';
    if (target.redisType === 'hash') {
      legacyForm.fullKey = target.key || '';
      legacyForm.valueType = 'hash';
    } else {
      legacyForm.fullKey = target.key || '';
      legacyForm.valueType = target.redisType || 'string';
    }
    legacyForm.value = getDefaultValueByTarget(target, legacyForm.valueType);
    legacyDialogVisible.value = true;
  };

  const openLegacyEdit = (row) => {
    if (!row) return;
    resetLegacyForm();
    legacyReadOnly.value = false;
    legacyEditMode.value = 'edit';
    legacyForm.fullKey = row.fullKey || '';
    legacyForm.valueType = row.valueType || row.redisType || 'string';
    legacyForm.field = row.field || '';
    legacyForm.oldField = row.field || '';
    legacyForm.member = row.member || (legacyForm.valueType === 'set' ? row.value || '' : '');
    legacyForm.oldMember = legacyForm.member;
    legacyForm.value = typeof row.value === 'string' ? row.value : JSON.stringify(row.value ?? '', null, 2);
    legacyForm.deleteWholeKey = Boolean(row.deleteWholeKey);
    legacyDialogVisible.value = true;
  };

  const openLegacyView = (row) => {
    if (!row) return;
    resetLegacyForm();
    legacyReadOnly.value = true;
    legacyEditMode.value = 'view';
    legacyForm.fullKey = row.fullKey || '';
    legacyForm.valueType = row.valueType || row.redisType || 'string';
    legacyForm.field = row.field || '';
    legacyForm.oldField = row.field || '';
    legacyForm.member = row.member || (legacyForm.valueType === 'set' ? row.value || '' : '');
    legacyForm.oldMember = legacyForm.member;
    legacyForm.value = formatLegacyValueForView(row.value);
    legacyForm.deleteWholeKey = Boolean(row.deleteWholeKey);
    legacyDialogVisible.value = true;
  };

  const buildLegacySavePayload = () => {
    const fullKey = trimValue(legacyForm.fullKey);
    const valueType = trimValue(legacyForm.valueType || 'string');
    if (!fullKey) throw new Error('fullKey 不能为空');
    if (valueType === 'hash') {
      const field = trimValue(legacyForm.field);
      if (!field) throw new Error('Hash field 不能为空');
      return {
        fullKey,
        valueType,
        field,
        value: legacyForm.value ?? '',
      };
    }
    if (valueType === 'set') {
      const member = trimValue(legacyForm.member || legacyForm.value);
      if (!member) throw new Error('Set member 不能为空');
      return {
        fullKey,
        valueType,
        oldMember: legacyEditMode.value === 'edit' ? legacyForm.oldMember : '',
        member,
      };
    }
    if (valueType === 'string') {
      return {
        fullKey,
        valueType,
        value: legacyForm.value ?? '',
      };
    }
    throw new Error('当前类型暂不支持保存');
  };

  const submitLegacySave = async () => {
    let payload;
    try {
      payload = buildLegacySavePayload();
    } catch (error) {
      ElMessage.error(error.message || '保存内容校验失败');
      return;
    }

    try {
      await ElMessageBox.confirm(
        `确定${legacyEditMode.value === 'add' ? '新增' : '保存'}日志及其他数据吗？\n\n${payload.fullKey}`,
        '确认操作',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        },
      );
    } catch {
      return;
    }

    legacySaving.value = true;
    try {
      const response = await mazdaPost('legacyRedisSave', payload);
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '日志及其他数据保存失败');
        return;
      }
      legacyDialogVisible.value = false;
      ElMessage.success(data.message || '日志及其他数据保存成功');
      await refreshLegacyAll();
    } catch (error) {
      ElMessage.error(buildRequestErrorMessage('日志及其他数据保存失败', error));
    } finally {
      legacySaving.value = false;
    }
  };

  const buildLegacyDeletePayload = (row) => {
    if (!row?.fullKey) throw new Error('fullKey 为空，无法删除');
    if (row.deleteWholeKey) {
      return { fullKey: row.fullKey, deleteType: 'key' };
    }
    if (row.valueType === 'hash') {
      if (!row.field) throw new Error('Hash field 为空，无法删除');
      return { fullKey: row.fullKey, deleteType: 'hashField', field: row.field };
    }
    if (row.valueType === 'set') {
      const member = row.member || row.value;
      if (!member) throw new Error('Set member 为空，无法删除');
      return { fullKey: row.fullKey, deleteType: 'setMember', member };
    }
    return { fullKey: row.fullKey, deleteType: 'key' };
  };

  const deleteLegacyRow = async (row) => {
    let payload;
    try {
      payload = buildLegacyDeletePayload(row);
    } catch (error) {
      ElMessage.error(error.message || '删除内容校验失败');
      return;
    }

    const suffix = payload.deleteType === 'key' ? '\n\n注意：这会删除整个 Redis Key。' : '';
    try {
      await ElMessageBox.confirm(
        `确定删除日志及其他数据吗？\n\n${payload.fullKey}${suffix}`,
        '确认删除',
        {
          confirmButtonText: '确定删除',
          cancelButtonText: '取消',
          type: 'warning',
        },
      );
    } catch {
      return;
    }

    legacySaving.value = true;
    try {
      const response = await mazdaPost('legacyRedisDelete', payload);
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '日志及其他数据删除失败');
        return;
      }
      ElMessage.success(data.message || '日志及其他数据删除成功');
      await refreshLegacyAll();
    } catch (error) {
      ElMessage.error(buildRequestErrorMessage('日志及其他数据删除失败', error));
    } finally {
      legacySaving.value = false;
    }
  };

  watch(() => legacyForm.valueType, (type) => {
    if (!legacyDialogVisible.value || legacyEditMode.value !== 'add') return;
    const target = selectedLegacyTarget.value;
    if (!trimValue(legacyForm.value)) {
      legacyForm.value = getDefaultValueByTarget(target, type);
    }
  });

  watch(serverName, refreshLegacyAll);
  onMounted(refreshLegacyAll);

  return {
    legacyOverview,
    legacyTargetId,
    legacyTargetOptions,
    selectedLegacyTarget,
    legacyItems,
    legacyTotal,
    legacyPage,
    legacyPageSize,
    legacyKeyword,
    legacyJumpPage,
    legacyCursor,
    legacyNextCursor,
    legacyCursorHistory,
    legacyHasMore,
    legacyScanMode,
    legacyExactTotal,
    legacyNotice,
    legacyFieldExact,
    legacyCanUseFieldExact,
    legacyPagerLabel,
    legacyTotalPages,
    legacyLoading,
    legacySaving,
    legacyReadOnly,
    legacyDialogVisible,
    legacyEditMode,
    legacyDialogTitle,
    legacyForm,
    legacyAllowAdd,
    legacyValueTypeOptions,
    legacyCanSave,
    legacyFullKeyReadonly,
    legacyFieldReadonly,
    refreshLegacyAll,
    loadLegacyItems,
    handleLegacyTargetChange,
    selectLegacyTarget,
    handleLegacySearch,
    handleLegacyPageChange,
    handleLegacySizeChange,
    handleLegacyJump,
    handleLegacyNextBatch,
    handleLegacyPrevBatch,
    openLegacyAdd,
    openLegacyView,
    openLegacyEdit,
    submitLegacySave,
    deleteLegacyRow,
  };
}
